import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAdminUsers } from '@/queries/admin';
import { UserListItem } from '@/components/admin/UserListItem';
import { UserEditDrawer } from '@/components/admin/UserEditDrawer';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import type { SubscriptionTier } from '@/types/subscription';
import styles from './users.module.scss';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdminStatus();

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate({ to: '/' });
    }
  }, [isAdmin, adminLoading, navigate]);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | undefined>(undefined);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data, isLoading, error } = useAdminUsers({
    page,
    limit: 20,
    search,
    tier: tierFilter,
  });

  if (adminLoading) {
    return <div className={styles.adminUsers__loading}>Checking permissions...</div>;
  }

  if (!isAdmin) {
    return null; // prevent flash content while redirecting
  }

  return (
    <div className={styles.adminUsers}>
      <div className={styles.adminUsers__header}>
        <h1 className={styles.adminUsers__title}>User Management</h1>
        <p className={styles.adminUsers__subtitle}>
          Manage user subscriptions and permissions
        </p>
      </div>

      {/* Search and Filters */}
      <div className={styles.adminUsers__filters}>
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // Reset to first page on search
          }}
          className={styles.adminUsers__searchInput}
        />

        <select
          value={tierFilter || ''}
          onChange={(e) => {
            setTierFilter(e.target.value as SubscriptionTier || undefined);
            setPage(1);
          }}
          className={styles.adminUsers__tierFilter}
        >
          <option value="">All Tiers</option>
          <option value="foundation">Foundation</option>
          <option value="deep_dive">Deep Dive</option>
          <option value="mastermind">Mastermind</option>
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={styles.adminUsers__loading}>
          Loading users...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={styles.adminUsers__error}>
          Error loading users: {error.message}
        </div>
      )}

      {/* Users List */}
      {data && (
        <>
          <div className={styles.adminUsers__list}>
            {data.users.length === 0 ? (
              <div className={styles.adminUsers__empty}>
                No users found matching your criteria
              </div>
            ) : (
              data.users.map((user) => (
                <UserListItem
                  key={user.user_id}
                  user={user}
                  onEdit={() => setSelectedUserId(user.user_id)}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          <div className={styles.adminUsers__pagination}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className={styles.adminUsers__paginationButton}
            >
              Previous
            </button>

            <span className={styles.adminUsers__paginationInfo}>
              Page {page} of {data.pagination.totalPages} ({data.pagination.total} users)
            </span>

            <button
              disabled={page >= data.pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
              className={styles.adminUsers__paginationButton}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Edit Drawer */}
      <UserEditDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
      />
    </div>
  );
}
